import { Spacing } from '@persona-id/design-tokens';
import { PlusIcon, TrashIcon } from '@persona-id/design-tokens/icons';
import { pull, set } from 'lodash/fp';
import { ChangeEvent } from 'react';
import styled from 'styled-components';

import Divider from 'dashboard/components/Divider';
import ReactSelect from 'dashboard/components/ReactSelect';
import { ProductFeatureName } from 'dashboard/models/organization';
import { SlaFrequency, UserNotificationPolicy } from 'dashboard/models/user';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Checkbox from 'design-system/components/Checkbox';
import Input from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import { Paragraph, Span } from 'design-system/components/Text';
import useProductFeature from 'lib/hooks/useProductFeature';
import { assertNever } from 'lib/utils/types';

const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr 0.15fr 0.15fr;
  justify-items: center;
`;

const LeftAlign = styled.div`
  justify-self: start;
  width: 100%;
`;

const StyledDivider = styled(Divider)`
  margin-top: 0;
  margin-bottom: 0;
`;

const SlaFrequencyContainer = styled.div`
  display: flex;
  column-gap: ${Spacing[2]};
  padding-top: ${Spacing[1]};
  padding-bottom: ${Spacing[1]};
`;

const SlaTypeSelect = styled(ReactSelect<string>)`
  max-width: 210px;
`;

const SlaTimeInput = styled(Input)`
  max-width: 100px;
  height: 100%;
`;

const SlaTimeSelect = styled(ReactSelect<string>)`
  max-width: 150px;
`;

const SlaFrequencySpan = styled(Span)`
  padding-top: ${Spacing[1]};
`;

const SLA_FREQUENCY_OPTIONS = [
  {
    label: 'a policy is late by',
    value: 'after',
  },
  {
    label: 'when time remaining is',
    value: 'before',
  },
];

const SLA_TIME_OPTIONS = ['days', 'minutes', 'hours'].map((unit) => {
  return { label: unit, value: unit };
});

interface TimeUnit {
  time: number;
  type: 'minutes' | 'hours' | 'days';
}

const timeUnitToSeconds = ({ time, type }: TimeUnit) => {
  switch (type) {
    case 'minutes':
      return time * 60;
    case 'hours':
      return time * 3600;
    case 'days':
      return time * 86400;
    default:
      assertNever(type);
  }
};

/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
const secondsToTimeUnit = (seconds: number): TimeUnit => {
  if (seconds % 86400 === 0) {
    return {
      time: seconds / 86400,
      type: 'days',
    };
  } else if (seconds % 3600 === 0) {
    return {
      time: seconds / 3600,
      type: 'hours',
    };
  } else if (seconds % 60 === 0) {
    return {
      time: seconds / 60,
      type: 'minutes',
    };
  }
};

export interface UserNotificationSettingsUpdateData {
  userNotificationPolicy?: UserNotificationPolicy;
}

interface UserNotificationSettingsProps {
  userNotificationPolicy: UserNotificationPolicy;
  updateUserNotificationSettings: (values: UserNotificationSettingsUpdateData) => Promise<void>;
}

const UserNotificationSettings = ({
  userNotificationPolicy,
  updateUserNotificationSettings,
}: UserNotificationSettingsProps) => {
  const slaNotificationsEnabled =
    userNotificationPolicy.case.sla['delivery-mechanism'].includes('dashboard');
  const hasCasesEnhanced = useProductFeature(ProductFeatureName.CasesEnhanced);
  const hasCasesEnterprise = useProductFeature(ProductFeatureName.CasesEnterprise);
  const updateCaseAssignmentEmailPolicy = () => {
    let nextAssignmentDeliveryMethods = userNotificationPolicy.case.assignment;
    if (nextAssignmentDeliveryMethods.includes('email')) {
      nextAssignmentDeliveryMethods = pull('email', nextAssignmentDeliveryMethods);
    } else {
      nextAssignmentDeliveryMethods = [...nextAssignmentDeliveryMethods, 'email'];
    }

    updateUserNotificationSettings({
      userNotificationPolicy: {
        ...userNotificationPolicy,
        case: {
          ...userNotificationPolicy.case,
          assignment: nextAssignmentDeliveryMethods,
        },
      },
    });
  };

  const updateCaseAssignmentDashboardPolicy = () => {
    let nextAssignmentDeliveryMethods = userNotificationPolicy.case.assignment;
    if (nextAssignmentDeliveryMethods.includes('dashboard')) {
      nextAssignmentDeliveryMethods = pull('dashboard', nextAssignmentDeliveryMethods);
    } else {
      nextAssignmentDeliveryMethods = [...nextAssignmentDeliveryMethods, 'dashboard'];
    }

    updateUserNotificationSettings({
      userNotificationPolicy: {
        ...userNotificationPolicy,
        case: {
          ...userNotificationPolicy.case,
          assignment: nextAssignmentDeliveryMethods,
        },
      },
    });
  };

  const updateCaseSlaDashboardPolicy = () => {
    let nextSlaDeliverymechanism = userNotificationPolicy.case.sla['delivery-mechanism'];
    if (nextSlaDeliverymechanism.includes('dashboard')) {
      nextSlaDeliverymechanism = pull('dashboard', nextSlaDeliverymechanism);
    } else {
      nextSlaDeliverymechanism = [...nextSlaDeliverymechanism, 'dashboard'];
    }

    updateUserNotificationSettings({
      userNotificationPolicy: {
        ...userNotificationPolicy,
        case: {
          ...userNotificationPolicy.case,
          sla: {
            ...userNotificationPolicy.case.sla,
            'delivery-mechanism': nextSlaDeliverymechanism,
          },
        },
      },
    });
  };

  const updateSlaFrequency = (index: number, frequency: SlaFrequency) => {
    const nextSlaFrequencies = set(
      [index],
      frequency,
      userNotificationPolicy.case.sla['frequency'],
    );

    updateUserNotificationSettings({
      userNotificationPolicy: {
        ...userNotificationPolicy,
        case: {
          ...userNotificationPolicy.case,
          sla: {
            ...userNotificationPolicy.case.sla,
            frequency: nextSlaFrequencies,
          },
        },
      },
    });
  };

  const addSlaFrequency = () => {
    updateUserNotificationSettings({
      userNotificationPolicy: {
        ...userNotificationPolicy,
        case: {
          ...userNotificationPolicy.case,
          sla: {
            ...userNotificationPolicy.case.sla,
            frequency: [
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              ...userNotificationPolicy.case.sla.frequency,
              {
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                repeat: null,
                type: 'before',
                time: 0,
              },
            ],
          },
        },
      },
    });
  };

  const deleteSlaFrequency = (index: number) => {
    const nextSlaFrequencies = [
      ...userNotificationPolicy.case.sla['frequency'].slice(0, index),
      ...userNotificationPolicy.case.sla['frequency'].slice(index + 1),
    ];

    updateUserNotificationSettings({
      userNotificationPolicy: {
        ...userNotificationPolicy,
        case: {
          ...userNotificationPolicy.case,
          sla: {
            ...userNotificationPolicy.case.sla,
            frequency: nextSlaFrequencies,
          },
        },
      },
    });
  };

  return (
    <Card>
      <Card.Body>
        <Card.Title>Notifications</Card.Title>
        <>
          <Container>
            <LeftAlign>
              <Card.Title>Cases</Card.Title>
            </LeftAlign>
            <Paragraph color='primary'>Email</Paragraph>
            <Paragraph color='primary'>Dashboard</Paragraph>
          </Container>
          <StyledDivider full />
          <Container>
            <LeftAlign>
              <Paragraph color='primary'>Case assignment</Paragraph>
              <Paragraph>Receive a notification when a case is assigned to you.</Paragraph>
            </LeftAlign>
            <Checkbox
              id='user-notification-settings__checkbox--case-assignment-email'
              aria-label='Receive an email notification for case assignment.'
              checked={userNotificationPolicy.case.assignment.includes('email')}
              onChange={updateCaseAssignmentEmailPolicy}
            />
            <Checkbox
              id='user-notification-settings__checkbox--case-assignment-dashboard'
              aria-label='Receive a dashboard notification for case assignment.'
              checked={userNotificationPolicy.case.assignment.includes('dashboard')}
              onChange={updateCaseAssignmentDashboardPolicy}
            />
          </Container>
          <StyledDivider full />
          {(hasCasesEnhanced || hasCasesEnterprise) && (
            <>
              <Container>
                <LeftAlign>
                  <Paragraph color='primary'>SLA breach</Paragraph>
                  <Paragraph>Receive a notification when an SLA policy is breached.</Paragraph>
                  {slaNotificationsEnabled && (
                    <>
                      {userNotificationPolicy.case.sla['frequency'].map((frequency, index) => {
                        const { time: slaTime, type: slaType } = secondsToTimeUnit(
                          frequency['time'],
                        );
                        return (
                          <SlaFrequencyContainer key={`sla-policy-${index}`}>
                            <SlaFrequencySpan>Notify me when</SlaFrequencySpan>
                            <SlaTypeSelect
                              options={SLA_FREQUENCY_OPTIONS}
                              value={frequency.type}
                              onChange={(value: 'before' | 'after') => {
                                updateSlaFrequency(index, {
                                  ...frequency,
                                  type: value,
                                });
                              }}
                              size='sm'
                              aria-label='Select before or after SLA breech'
                            />
                            <SlaTimeInput
                              value={slaTime}
                              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                const nextSlaSeconds = timeUnitToSeconds({
                                  time: Math.floor(Number(e.target.value)),
                                  type: slaType,
                                });
                                if (nextSlaSeconds >= 0 && nextSlaSeconds <= 2678400) {
                                  updateSlaFrequency(index, {
                                    ...frequency,
                                    time: nextSlaSeconds,
                                  });
                                }
                              }}
                              size='md'
                            />
                            <SlaTimeSelect
                              value={slaType}
                              options={SLA_TIME_OPTIONS}
                              size='sm'
                              onChange={(value: 'minutes' | 'hours' | 'days') => {
                                const nextSlaSeconds = timeUnitToSeconds({
                                  time: slaTime,
                                  type: value,
                                });
                                if (nextSlaSeconds >= 0 && nextSlaSeconds <= 2678400) {
                                  updateSlaFrequency(index, {
                                    ...frequency,
                                    time: nextSlaSeconds,
                                  });
                                }
                              }}
                              disabled={slaTime === 0}
                            />
                            <Button
                              id={`user-notification-settings__button-case-sla-delete-notification-${index}`}
                              width='icon'
                              variant='inverse'
                              onClick={() => deleteSlaFrequency(index)}
                              disabled={userNotificationPolicy.case.sla['frequency'].length === 1}
                              aria-label='Delete SLA notification policy'
                            >
                              <TrashIcon />
                            </Button>
                          </SlaFrequencyContainer>
                        );
                      })}
                      <Spacer height={1} />
                      <Button
                        id='user-notification-settings__button-case-sla-add-notification'
                        prefixIcon={<PlusIcon />}
                        variant='basic'
                        onClick={addSlaFrequency}
                        width='auto'
                      >
                        Add notification
                      </Button>
                    </>
                  )}
                </LeftAlign>
                <Checkbox
                  id='user-notification-settings__checkbox--case-sla-email'
                  aria-label='Receive an email notification for case SLA.'
                  disabled={true}
                />
                <Checkbox
                  id='user-notification-settings__checkbox--case-sla-dashboard'
                  aria-label='Receive a dashboard notification for case SLA.'
                  checked={userNotificationPolicy.case.sla['delivery-mechanism'].includes(
                    'dashboard',
                  )}
                  onChange={updateCaseSlaDashboardPolicy}
                />
              </Container>
              <StyledDivider full />
            </>
          )}
        </>
      </Card.Body>
    </Card>
  );
};

export default UserNotificationSettings;
